import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { issue__EventType } from "@medbillai/generated-graphql-types";

import { formatCurrency } from "@medbillai/utils/currency";

import { attentionActionLabel } from "../../home/sections/attentionActionLabel";
import { rankHeadlineAction } from "../../home/sections/headlineAction";
import {
  isResolvedClosure,
  isUserResolved,
} from "../../home/sections/resolveClosedWin";
import {
  attentionChip,
  type CardAttention,
  caseStatus,
  isCardAttention,
  NOTHING_FLAGGED,
  type RecordKind,
  recordAttention,
  STATUS_CHIP,
} from "./records";

dayjs.extend(utc);

export type CaseFacts = {
  readonly closed: boolean;
  readonly hasActions: boolean;
  readonly hasUnseenStaffMessage: boolean;
  readonly openedAt: Date | string;
  readonly closedAt?: Date | string | null;
  readonly isEscalated: boolean;
  readonly escalatedAt?: Date | string | null;
  readonly assigneeName?: string | null;
  readonly actions: readonly {
    readonly eventType: issue__EventType;
    readonly actionRequired: boolean;
    readonly sentAt: Date | string;
  }[];
  readonly caseManagementClosureReason?: string | null;
  readonly userClosureReason?: string | null;
  readonly reviewed: boolean;
  readonly dollarAmountSaved?: string | null;
  readonly recordBillAmount?: string | null;
  readonly negotiatedBillAmount?: string | null;
};

export type RecordFacts = {
  readonly kind: RecordKind;
  readonly uploaded: boolean;
  readonly receivedAt?: Date | string;
  readonly payer?: string;
  /** The bill's provider or the notice's collector. */
  readonly provider?: string;
  readonly denied?: boolean;
  readonly partlyDenied?: boolean;
  readonly pending?: boolean;
  readonly pharmacy?: boolean;
  readonly share?: number;
  readonly delinquent?: boolean;
  readonly inCollections?: boolean;
  /** Calendar dates, YYYY-MM-DD. */
  readonly dueDate?: string;
  readonly deadline?: string;
  /** A statement's or notice's own date, YYYY-MM-DD. */
  readonly documentDate?: string;
  /** The visit, YYYY-MM-DD; claims and bills only. */
  readonly serviceDate?: string;
  readonly outOfNetwork?: boolean;
};

export type CaseCardState =
  | "closedNoteUnseen"
  | "resolvedSaved"
  | "resolvedNoSavings"
  | "resolvedByUser"
  | "closedNoResponse"
  | "closedOther"
  | "needsReply"
  | "needsRequest"
  | "newMessage"
  | "advocateWorking"
  | "justEscalated"
  | "quiet";

type Step = {
  readonly date: string;
  readonly text: string;
  readonly warning?: string;
};

type DatedStep = Omit<Step, "date"> & { readonly at: Date | string };

export type StatusCard = {
  readonly state?: CaseCardState;
  readonly chip: {
    readonly label: string;
    readonly tone: "negative" | "warning" | "neutral" | "positive" | "info";
  };
  readonly headline: string;
  /** Follows the headline in the positive colour. */
  readonly saved?: string;
  readonly compact?: boolean;
  readonly body?: string;
  readonly footer?: string;
  /** Oldest first. */
  readonly steps: readonly Step[];
  readonly marker?: "progress" | "needsYou" | "done";
  readonly assigneeName?: string;
  /** The case's action. Without a case the record's own action applies. */
  readonly button?: {
    readonly label: string;
    readonly variant: "brand" | "secondary";
    readonly quiet?: boolean;
  };
};

const DOCUMENT_WORD: Record<RecordKind, string> = {
  claim: "EOB",
  invoice: "bill",
  statement: "statement",
  collectionsNotice: "notice",
};

const ADVOCATE_REPLY = "Advocates usually reply within 1 to 2 business days.";
const OPEN_CASE = { label: "Open the case", variant: "secondary" } as const;

const day = (date: Date | string): string => dayjs.utc(date).format("MMM D");

export const dollars = (amount: number): string =>
  formatCurrency(amount, "USD", { maximumFractionDigits: 0 });

/** The resolved-with-savings card for a record whose own amount can't show the saving. */
export const onCaseSaving = (
  card: StatusCard,
  facts: CaseFacts,
): StatusCard => ({
  ...card,
  headline: "Granted saved",
  saved: dollars(Number.parseFloat(facts.dollarAmountSaved ?? "")),
  body: [
    facts.closedAt ? `Resolved ${day(facts.closedAt)}` : "Resolved",
    "on this case",
  ].join(" · "),
});

const isNoResponse = (reason: string | null | undefined): boolean =>
  reason?.trim().toLowerCase() === "no response";

const recordSteps = (
  record: RecordFacts,
  today: Date | string,
): DatedStep[] => {
  const received = record.receivedAt;
  const plan = record.payer || "Your plan";
  const receipt: DatedStep[] = !received
    ? []
    : record.kind === "claim" && record.denied
      ? record.uploaded
        ? [
            { at: received, text: "You uploaded the EOB" },
            { at: received, text: `${plan} denied it` },
          ]
        : [{ at: received, text: `${plan} denied the claim` }]
      : record.uploaded
        ? [
            {
              at: received,
              text: `You uploaded the ${DOCUMENT_WORD[record.kind]}`,
            },
          ]
        : record.kind === "claim"
          ? [
              {
                at: received,
                text: `${record.payer || "Your insurer"} processed the claim`,
              },
            ]
          : [];
  const from = record.provider ? ` from ${record.provider}` : "";
  const steps: DatedStep[] = [
    ...(record.serviceDate
      ? [
          {
            at: record.serviceDate,
            text: record.provider ? `Visit at ${record.provider}` : "Visit",
            warning: record.outOfNetwork ? ", out of network" : undefined,
          },
        ]
      : []),
    ...(record.documentDate
      ? [
          {
            at: record.documentDate,
            text: `${record.kind === "statement" ? "Statement" : "Notice"}${from}`,
          },
        ]
      : []),
    ...receipt,
  ];
  if (record.delinquent && record.dueDate) {
    steps.push({ at: record.dueDate, text: "Payment was due" });
  }
  if (record.deadline && recordAttention(record, today) === "deadlinePassed") {
    steps.push({ at: record.deadline, text: "Response was due" });
  }
  return steps;
};

const deniedCopy = (record: RecordFacts) => ({
  headline: "This claim was denied",
  body: record.share
    ? `You may owe ${formatCurrency(record.share)}. Granted can appeal it first.`
    : "Don’t pay yet. Granted can appeal it.",
});

const ATTENTION_COPY: Record<
  CardAttention,
  (record: RecordFacts) => { headline: string; body: string }
> = {
  denied: deniedCopy,
  partlyDenied: deniedCopy,
  inCollections: record => ({
    headline: "This bill went to collections",
    body: `${record.provider ?? "The provider"} sent it to a collector. Granted can help you fight it.`,
  }),
  overdue: record => ({
    headline: "This bill is overdue",
    body: `${record.provider ?? "The provider"} marked it delinquent. Granted can ask for more time.`,
  }),
  deadlinePassed: () => ({
    headline: "The response deadline has passed",
    body: "The deadline to respond has passed. Granted can still help you fight it.",
  }),
  respondBy: record => ({
    headline: "A collector wants a response",
    body: `Granted can answer ${record.provider ?? "the collector"} for you.`,
  }),
};

const QUIET_HEADLINE: Record<RecordKind, string> = {
  claim: "Got the bill for this visit?",
  invoice: "Think this bill is wrong?",
  statement: "Something off on this statement?",
  collectionsNotice: "Want Granted to handle this?",
};

const recordCard = (record: RecordFacts, today: Date | string): StatusCard => {
  const attention = recordAttention(record, today);
  return isCardAttention(attention)
    ? {
        chip: attentionChip(attention, record.deadline),
        ...ATTENTION_COPY[attention](record),
        steps: [],
      }
    : {
        chip: NOTHING_FLAGGED,
        headline: record.pharmacy
          ? "Something off on this claim?"
          : QUIET_HEADLINE[record.kind],
        steps: [],
      };
};

/** First match wins. */
export const caseCard = (
  facts: CaseFacts | null,
  record: RecordFacts,
  today: Date | string,
): StatusCard => {
  if (!facts) {
    return recordCard(record, today);
  }
  const name = facts.assigneeName?.trim().split(/\s+/)[0] || undefined;
  const holder = (facts.isEscalated && name) || "Granted";
  const closedOn = facts.closedAt ? day(facts.closedAt) : "";
  const resolved = isResolvedClosure(facts);
  const noResponse = isNoResponse(facts.caseManagementClosureReason);
  const saved = Number.parseFloat(facts.dollarAmountSaved ?? "");
  const actions = facts.actions.map(action => ({
    ...action,
    sentAt: new Date(action.sentAt).toISOString(),
  }));
  const { headline: ask } = rankHeadlineAction(actions);

  const dated: DatedStep[] = [
    ...recordSteps(record, today),
    { at: facts.openedAt, text: "Granted started looking into it" },
    ...(facts.isEscalated && facts.escalatedAt
      ? [
          {
            at: facts.escalatedAt,
            text: name
              ? `${name} took the case`
              : "Granted brought in an advocate",
          },
        ]
      : []),
    ...(facts.closed && facts.closedAt
      ? [
          {
            at: facts.closedAt,
            text: resolved
              ? "Resolved"
              : noResponse
                ? "Closed, no response"
                : "Closed",
          },
        ]
      : []),
  ];
  // Stable, so same-day steps keep the order they happen in.
  const steps = dated
    .sort((a, b) => dayjs.utc(a.at).valueOf() - dayjs.utc(b.at).valueOf())
    .map(({ at, ...step }) => ({ date: day(at), ...step }));
  const review = facts.reviewed
    ? { ...OPEN_CASE, quiet: false }
    : { label: "Leave a review", variant: "brand" as const, quiet: true };
  const card = (
    state: CaseCardState,
    button: NonNullable<StatusCard["button"]>,
    headline: string,
    body?: string,
    footer?: string,
  ): StatusCard => ({
    state,
    chip: STATUS_CHIP[caseStatus(facts)],
    headline,
    compact: state === "quiet",
    body,
    footer,
    steps,
    marker: facts.closed
      ? "done"
      : state === "needsReply" || state === "needsRequest"
        ? "needsYou"
        : "progress",
    assigneeName: facts.assigneeName ?? undefined,
    button,
  });

  if (facts.closed) {
    if (facts.hasUnseenStaffMessage) {
      return card(
        "closedNoteUnseen",
        { label: "Read it", variant: "brand" },
        `${holder} left a note`,
        `${holder} closed this case on ${closedOn}.`,
      );
    }
    if (resolved && saved > 0) {
      return {
        ...card(
          "resolvedSaved",
          review,
          "Granted saved you",
          `Resolved ${closedOn}.`,
        ),
        saved: dollars(saved),
      };
    }
    // Nobody reviewed a case the user closed as resolved.
    if (isUserResolved(facts)) {
      return card(
        "resolvedByUser",
        review,
        "You marked this case resolved",
        `Resolved ${closedOn}.`,
      );
    }
    if (resolved) {
      return card(
        "resolvedNoSavings",
        review,
        "No issues found",
        `${name ?? "Granted"} checked it and found nothing to fix. Resolved ${closedOn}.`,
      );
    }
    if (noResponse) {
      return card(
        "closedNoResponse",
        { label: "Pick this back up", variant: "secondary" },
        "Closed, no response",
        `Granted closed this on ${closedOn} after not hearing back.`,
      );
    }
    return card(
      "closedOther",
      OPEN_CASE,
      "This case is closed",
      `Closed ${closedOn}.`,
    );
  }
  if (ask?.eventType === issue__EventType.CHAT) {
    return card(
      "needsReply",
      { label: "Reply", variant: "brand" },
      "Respond to your case",
      `${holder} sent you a message.`,
      `${holder} picks this back up as soon as you reply.`,
    );
  }
  if (ask) {
    const label = attentionActionLabel({ actions });
    return card(
      "needsRequest",
      { label, variant: "brand" },
      label,
      `${holder} needs this to keep going.`,
      `${holder} picks this back up as soon as you answer.`,
    );
  }
  if (facts.hasUnseenStaffMessage) {
    return card(
      "newMessage",
      { label: "Read it", variant: "brand" },
      `${holder} sent you an update`,
      "Read it when you can. There’s nothing for you to do yet.",
    );
  }
  if (facts.isEscalated && name) {
    return card(
      "advocateWorking",
      OPEN_CASE,
      `${name} is working on it`,
      `Nothing needed from you right now. ${name} will message you here.`,
      ADVOCATE_REPLY,
    );
  }
  if (facts.isEscalated) {
    return card(
      "justEscalated",
      OPEN_CASE,
      "An advocate will review this next",
      "They’ll message you here once they’ve looked.",
      ADVOCATE_REPLY,
    );
  }
  return card("quiet", OPEN_CASE, "Nothing needed from you");
};
